/**
 * Team Members Management Page
 * Quản lý danh sách nhân viên trong team
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  CheckCircle,
  Mail,
  Briefcase,
  Building2,
} from 'lucide-react';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { TeamMember, TeamMemberFormData } from '@/types/evaluation';
import { toast } from 'sonner';

const TeamMembersPage: React.FC = () => {
  const {
    teamMembers,
    activeTeamMembers,
    isLoading,
    error,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
  } = useTeamMembers();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<TeamMemberFormData>({
    employee_name: '',
    employee_email: '',
    employee_code: '',
    position: '',
    department: '',
    notes: '',
    is_active: true,
  });

  // Open dialog for creating new member
  const handleCreate = () => {
    setEditingMember(null);
    setFormData({
      employee_name: '',
      employee_email: '',
      employee_code: '',
      position: '',
      department: '',
      notes: '',
      is_active: true,
    });
    setIsDialogOpen(true);
  };

  // Open dialog for editing member
  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      employee_name: member.employee_name,
      employee_email: member.employee_email || '',
      employee_code: member.employee_code || '',
      position: member.position || '',
      department: member.department || '',
      notes: member.notes || '',
      is_active: member.is_active,
    });
    setIsDialogOpen(true);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.employee_name.trim()) {
      toast.error('Vui lòng nhập tên nhân viên');
      return;
    }

    if (!formData.department?.trim()) {
      toast.error('Vui lòng nhập phòng ban');
      return;
    }

    setIsSaving(true);

    try {
      if (editingMember) {
        await updateTeamMember(editingMember.id, formData);
        toast.success('Cập nhật nhân viên thành công');
      } else {
        await createTeamMember(formData);
        toast.success('Thêm nhân viên thành công');
      }
      setIsDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Có lỗi xảy ra');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (member: TeamMember) => {
    if (!confirm(`Bạn có chắc muốn xóa nhân viên "${member.employee_name}"?`)) {
      return;
    }

    try {
      await deleteTeamMember(member.id);
      toast.success('Xóa nhân viên thành công');
    } catch (err: any) {
      toast.error(err.message || 'Không thể xóa nhân viên');
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto px-3 sm:px-6 sm:max-w-6xl py-6 space-y-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Quản lý nhân viên</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto px-3 sm:px-6 sm:max-w-6xl py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Quản lý nhân viên</h1>
            <p className="text-sm text-gray-500 mt-1">
              Danh sách nhân viên trong team của bạn
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Thêm nhân viên
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tổng số</p>
                <p className="text-2xl font-bold">{teamMembers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Đang hoạt động</p>
                <p className="text-2xl font-bold">{activeTeamMembers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Không hoạt động</p>
                <p className="text-2xl font-bold">
                  {teamMembers.length - activeTeamMembers.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Members Table */}
      <Card>
        <CardHeader>
          <CardTitle>Danh sách nhân viên</CardTitle>
        </CardHeader>
        <CardContent>
          {teamMembers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Chưa có nhân viên nào</p>
              <Button onClick={handleCreate} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Thêm nhân viên đầu tiên
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tên nhân viên</TableHead>
                    <TableHead>Mã NV</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Chức vụ</TableHead>
                    <TableHead>Phòng ban</TableHead>
                    <TableHead>Trạng thái</TableHead>
                    <TableHead className="text-right">Thao tác</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {member.employee_name}
                      </TableCell>
                      <TableCell>
                        {member.employee_code || (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.employee_email ? (
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3 text-gray-400" />
                            <span className="text-sm">{member.employee_email}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.position ? (
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-3 w-3 text-gray-400" />
                            <span className="text-sm">{member.position}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.department ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="h-3 w-3 text-gray-400" />
                            <span className="text-sm">{member.department}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.is_active ? (
                          <Badge variant="default" className="bg-green-100 text-green-800">
                            Hoạt động
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Không hoạt động</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(member)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(member)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {editingMember ? 'Chỉnh sửa nhân viên' : 'Thêm nhân viên mới'}
              </DialogTitle>
              <DialogDescription>
                Nhập thông tin nhân viên trong team của bạn
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="employee_name">
                  Tên nhân viên <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="employee_name"
                  value={formData.employee_name}
                  onChange={(e) =>
                    setFormData({ ...formData, employee_name: e.target.value })
                  }
                  placeholder="Nguyễn Văn A"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="employee_code">Mã nhân viên</Label>
                  <Input
                    id="employee_code"
                    value={formData.employee_code}
                    onChange={(e) =>
                      setFormData({ ...formData, employee_code: e.target.value })
                    }
                    placeholder="NV001"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="employee_email">Email</Label>
                  <Input
                    id="employee_email"
                    type="email"
                    value={formData.employee_email}
                    onChange={(e) =>
                      setFormData({ ...formData, employee_email: e.target.value })
                    }
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="position">Chức vụ</Label>
                  <Input
                    id="position"
                    value={formData.position}
                    onChange={(e) =>
                      setFormData({ ...formData, position: e.target.value })
                    }
                    placeholder="Nhân viên"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="department">
                    Phòng ban <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) =>
                      setFormData({ ...formData, department: e.target.value })
                    }
                    placeholder="Phòng Kinh doanh"
                    required
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="notes">Ghi chú</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="Ghi chú thêm về nhân viên..."
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
                <Label htmlFor="is_active">Đang hoạt động</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={isSaving}
              >
                Hủy
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Đang lưu...' : editingMember ? 'Cập nhật' : 'Thêm mới'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamMembersPage;

